import { availableLocales, setLocale, type Locale } from '../i18n';
import { parseFabBottom, parseFabSide } from './fab';
import type { FabPosition, WidgetBootConfig } from '../types/public';

const DEFAULT_FAB: { desktop: FabPosition; mobile: FabPosition } = {
  desktop: { side: 'right', bottom: 20 },
  mobile: { side: 'right', bottom: 20 },
};

function readFab(query: URLSearchParams | null): {
  desktop: FabPosition;
  mobile: FabPosition;
} {
  if (!query) return DEFAULT_FAB;
  return {
    desktop: {
      side: parseFabSide(query.get('fab_desktop_side')) ?? DEFAULT_FAB.desktop.side,
      bottom: parseFabBottom(query.get('fab_desktop_bottom')) ?? DEFAULT_FAB.desktop.bottom,
    },
    mobile: {
      side: parseFabSide(query.get('fab_mobile_side')) ?? DEFAULT_FAB.mobile.side,
      bottom: parseFabBottom(query.get('fab_mobile_bottom')) ?? DEFAULT_FAB.mobile.bottom,
    },
  };
}

/**
 * Захватываем ссылку на свой `<script>` **синхронно при evaluation модуля**.
 *
 * Для `<script type="module">` `document.currentScript` всегда `null` (по спеке),
 * поэтому snapshot будет полезен только для классических (не-module) скриптов.
 * Для модульной загрузки используется DOM-поиск через `findBootScript()`.
 */
const CAPTURED_BOOT_SCRIPT = document.currentScript as HTMLScriptElement | null;

const BOOT_SCRIPT_ID = 'mpwc-widget-loader';

/**
 * Совпадает с URL'ами, по которым партнёры подключают bundle виджета:
 *  - `/mpwc/widget.js`, `/mpwcs/widget.js` (classic legacy filename)
 *  - `/mpwc/index-<hash>.js`, `/mpwcs/index-<hash>.js` (hashed bundle для cache-busting)
 *
 * НЕ матчится gtag.js, GTM, любые сторонние скрипты с `?id=` — это и был баг,
 * из-за которого мы подхватывали GA measurement ID вместо своего token'а.
 */
const WIDGET_BUNDLE_PATHNAME = /\/mpwcs?\/(widget\.js|index-[A-Za-z0-9_-]+\.js)$/;

function isWidgetBundleScript(s: HTMLScriptElement): boolean {
  if (!s.src) return false;
  try {
    return WIDGET_BUNDLE_PATHNAME.test(new URL(s.src).pathname);
  } catch {
    return false;
  }
}

/**
 * Partner'ы бывают двух типов:
 *
 *  1. Legacy (saimatelecom.kg, o.kg, ostore.kg): в HTML hardcoded
 *     `<script src=".../widget.js?id=TOKEN&lang=XX" type="module">`. Token
 *     лежит в query-string `?id=`.
 *
 *  2. Modern: используют `data-cpw-token` через dataset, опционально с
 *     `id="mpwc-widget-loader"` на самом теге.
 *
 * Поиск:
 *  1. Захваченный `document.currentScript` snapshot — для не-module скриптов
 *     (классический IIFE evaluation).
 *  2. `document.currentScript` сейчас — на случай повторной evaluation.
 *  3. По explicit HTML id (рекомендуемый паттерн для новых партнёров).
 *  4. DOM scan по pathname bundle'а (НЕ по `?id=` — иначе подхватим gtag.js).
 */
export function findBootScript(): HTMLScriptElement | null {
  if (CAPTURED_BOOT_SCRIPT && isWidgetBundleScript(CAPTURED_BOOT_SCRIPT)) {
    return CAPTURED_BOOT_SCRIPT;
  }
  const current = document.currentScript as HTMLScriptElement | null;
  if (current && isWidgetBundleScript(current)) return current;

  const byId = document.getElementById(BOOT_SCRIPT_ID);
  if (byId instanceof HTMLScriptElement) return byId;

  for (const s of document.scripts) {
    if (isWidgetBundleScript(s)) return s;
  }
  return null;
}

/**
 * Распознаёт чужие measurement-id (GA4 `G-XXXX`, Universal Analytics `UA-XXXX`,
 * Google Tag Manager `GTM-XXXX`, Google Ads `AW-XXXX`), чтобы не выстрелить
 * запросом `/api/widget/config/G-XXXX` с гарантированным 404. Защита от
 * residual случаев — если ошибётся `findBootScript()` или партнёр сам передаст
 * мусор через `data-cpw-token`.
 */
const FOREIGN_TOKEN_PATTERNS: readonly RegExp[] = [
  /^G-[A-Z0-9]+$/i,
  /^GTM-[A-Z0-9]+$/i,
  /^UA-\d+(-\d+)?$/i,
  /^AW-\d+$/i,
];

export function isLikelyPartnerToken(token: string): boolean {
  if (!token) return false;
  return !FOREIGN_TOKEN_PATTERNS.some((re) => re.test(token));
}

/**
 * Читает boot-конфиг из HTML и возвращает `{ token, apiUrl }`.
 *
 * Side-effect: если `?lang=` / `data-cpw-locale` / `__CPW_CONFIG__.locale`
 * задают поддерживаемый locale — вызывается `setLocale(locale, false)` до
 * return'а, чтобы первый render использовал правильный язык. Идемпотентно
 * при повторном вызове с тем же input'ом (no-op на том же locale).
 */
export function readBootConfig(): WidgetBootConfig {
  const bootScript = findBootScript();
  const w = window as typeof window & {
    __CPW_CONFIG__?: Partial<WidgetBootConfig> & { locale?: string };
  };
  const ds = bootScript?.dataset ?? ({} as DOMStringMap);

  const scriptUrl = bootScript?.src;
  const scriptQuery = scriptUrl ? new URL(scriptUrl).searchParams : null;
  const bundleOrigin = scriptUrl ? new URL(scriptUrl).origin : window.location.origin;

  const legacyToken = scriptQuery?.get('id') ?? undefined;
  const legacyLocale = scriptQuery?.get('lang') ?? undefined;

  const wanted = (ds.cpwLocale ?? w.__CPW_CONFIG__?.locale ?? legacyLocale) as
    | string
    | undefined;
  if (wanted && (availableLocales as readonly string[]).includes(wanted)) {
    // Partner-hardcoded locale перекрывает авто-пик.
    setLocale(wanted as Locale, false);
  }

  const envApiUrl = import.meta.env.VITE_PUBLIC_API_URL;

  const rawToken = ds.cpwToken ?? w.__CPW_CONFIG__?.token ?? legacyToken ?? '';
  const token = isLikelyPartnerToken(rawToken) ? rawToken : '';
  if (rawToken && !token) {
    console.error(
      `[cpw] Boot token "${rawToken}" похож на сторонний measurement-id (GA/GTM/UA/AW). ` +
        'Не делаем запрос на /api/widget/config. Проверь подключение тега виджета.',
    );
  } else if (!bootScript) {
    console.error(
      '[cpw] Не удалось найти <script> виджета в DOM. ' +
        `Ожидаемый паттерн: <script id="${BOOT_SCRIPT_ID}" type="module" ` +
        'src="https://ccp.o.kg/mpwc/widget.js?id=YOUR_TOKEN">.',
    );
  }

  return {
    token,
    apiUrl:
      ds.cpwApiUrl ??
      w.__CPW_CONFIG__?.apiUrl ??
      envApiUrl ??
      bundleOrigin,
    fab: readFab(scriptQuery),
  };
}

